// The canvas scale and the shape/occupancy rules live in `@peles/shared`: the
// PDF exporter draws the same stored plan, and a second copy of any of them is
// a plan that renders one way on screen and another in the file a client is
// sent. Re-exported here so the canvas keeps importing from its own helpers.
import { ftToPx } from '@peles/shared';

export {
  PIXELS_PER_FOOT,
  ftToPx,
  pxToFt,
  tableOccupancyColor as getTableColor,
} from '@peles/shared';

export function snapToGrid(value: number, gridSize: number): number {
  return Math.round(value / gridSize) * gridSize;
}

/**
 * Where a plan's grid lines fall along one edge, in feet from the origin, and
 * which are the heavier major lines: every 5 feet on a grid of a foot or
 * less, every fifth line on a coarser one (every 10 ft on a 2 ft grid).
 */
export function gridLines(extentFt: number, gridFt: number): { ft: number; major: boolean }[] {
  if (!(gridFt > 0) || !(extentFt >= 0)) return [];
  const majorEvery = gridFt <= 1 ? Math.round(5 / gridFt) : 5;
  const count = Math.ceil(extentFt / gridFt - 1e-9);
  return Array.from({ length: count + 1 }, (_, i) => ({
    ft: i * gridFt,
    major: i % majorEvery === 0,
  }));
}

export const CHAIR_SIZE_FT = 1.5;

export function calcCeremonyPositions(
  preset: 'grid' | 'curved' | 'vshape',
  rows: number,
  chairsPerRow: number,
  chairSpacingFt: number,
  rowSpacingFt: number,
  withAisle: boolean,
  aisleWidthFt: number,
): { positions: { x: number; y: number }[]; widthFt: number; heightFt: number } {
  const cs = CHAIR_SIZE_FT;
  const cstep = cs + chairSpacingFt;
  const rstep = cs + rowSpacingFt;
  const positions: { x: number; y: number }[] = [];

  if (preset === 'curved') {
    const spreadAngle = Math.PI * 0.75;
    for (let r = 0; r < rows; r++) {
      const radius = (chairsPerRow * cstep) / spreadAngle + r * rstep;
      const cx = (chairsPerRow * cstep) / 2;
      const cy = radius;
      for (let c = 0; c < chairsPerRow; c++) {
        const a = Math.PI - spreadAngle / 2 + (spreadAngle / Math.max(chairsPerRow - 1, 1)) * c;
        positions.push({ x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a) });
      }
    }
    const minX = Math.min(...positions.map((p) => p.x));
    const minY = Math.min(...positions.map((p) => p.y));
    const norm = positions.map((p) => ({ x: p.x - minX, y: p.y - minY }));
    const maxX = Math.max(...norm.map((p) => p.x));
    const maxY = Math.max(...norm.map((p) => p.y));
    return { positions: norm, widthFt: maxX + cs, heightFt: maxY + cs };
  }

  if (preset === 'vshape') {
    const half = Math.floor(chairsPerRow / 2);
    const other = chairsPerRow - half;
    const gap = withAisle ? aisleWidthFt : cstep;
    for (let r = 0; r < rows; r++) {
      const inset = r * 1.0;
      for (let c = 0; c < half; c++) positions.push({ x: inset + c * cstep, y: r * rstep });
      const rightStart = half * cstep + gap;
      for (let c = 0; c < other; c++)
        positions.push({ x: rightStart + c * cstep - inset, y: r * rstep });
    }
  } else {
    // grid
    const half = Math.floor(chairsPerRow / 2);
    const other = chairsPerRow - half;
    for (let r = 0; r < rows; r++) {
      if (withAisle) {
        for (let c = 0; c < half; c++) positions.push({ x: c * cstep, y: r * rstep });
        const rightStart = half * cstep + aisleWidthFt;
        for (let c = 0; c < other; c++) positions.push({ x: rightStart + c * cstep, y: r * rstep });
      } else {
        for (let c = 0; c < chairsPerRow; c++) positions.push({ x: c * cstep, y: r * rstep });
      }
    }
  }

  const maxX = positions.length > 0 ? Math.max(...positions.map((p) => p.x)) : 0;
  const maxY = positions.length > 0 ? Math.max(...positions.map((p) => p.y)) : 0;
  return { positions, widthFt: maxX + cs, heightFt: maxY + cs };
}

// ─── Canvas orientation (display-only rotation / flip) ─────────
//
// Rotation and flip never touch object coordinates — they are applied as a
// single wrapper <g transform> over the canvas content. Both the producer
// canvas and the client-portal canvas derive their orientation from here.

export type CanvasRotation = 0 | 90 | 180 | 270;

export interface CanvasOrientation {
  /** viewBox width after rotation (width/height swap at 90/270). */
  viewW: number;
  /** viewBox height after rotation. */
  viewH: number;
  /** Wrapper <g> transform for the canvas content, or undefined when identity. */
  groupTransform: string | undefined;
}

export function rotateCanvas(rotation: CanvasRotation, direction: 1 | -1): CanvasRotation {
  return ((((rotation + direction * 90) % 360) + 360) % 360) as CanvasRotation;
}

export function getCanvasOrientation(
  widthPx: number,
  heightPx: number,
  rotation: CanvasRotation,
  flipH = false,
): CanvasOrientation {
  const swapped = rotation === 90 || rotation === 270;
  const viewW = swapped ? heightPx : widthPx;
  const viewH = swapped ? widthPx : heightPx;
  const parts: string[] = [];
  // Flip is applied in view space (outermost) so it mirrors what is on screen,
  // regardless of the current rotation.
  if (flipH) parts.push(`translate(${viewW}, 0) scale(-1, 1)`);
  if (rotation === 90) parts.push(`translate(${heightPx}, 0) rotate(90)`);
  else if (rotation === 180) parts.push(`translate(${widthPx}, ${heightPx}) rotate(180)`);
  else if (rotation === 270) parts.push(`translate(0, ${widthPx}) rotate(270)`);
  return { viewW, viewH, groupTransform: parts.length > 0 ? parts.join(' ') : undefined };
}

// ─── Rotation-aware bounding box ───────────────────────────────
//
// Items are stored and drawn as `translate(x, y) rotate(rotation, w/2, h/2)`:
// `x`/`y` are the top-left of the UN-rotated box, and the shape spins about
// its own center. So whenever width ≠ height and the rotation is not a
// multiple of 180°, the *visible* left/top edge no longer sits at `x`/`y` —
// e.g. a 12ft × 3ft table rotated 90° looks shifted ~4.5ft right of its
// stored `x`. These helpers convert between the stored anchor and the visible
// bounding box so the properties panel matches what the user sees on canvas.
// All values share the caller's unit (pixels at the current call sites).

/** Width/height of the axis-aligned bounding box of a rotated rectangle. */
export function rotatedAabbSize(
  width: number,
  height: number,
  rotationDeg: number,
): { width: number; height: number } {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  return {
    width: width * cos + height * sin,
    height: width * sin + height * cos,
  };
}

/** Stored anchor (un-rotated top-left) → visible top-left of the rotated box. */
export function anchorToVisualTopLeft(
  x: number,
  y: number,
  width: number,
  height: number,
  rotationDeg: number,
): { x: number; y: number } {
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  const box = rotatedAabbSize(width, height, rotationDeg);
  return { x: centerX - box.width / 2, y: centerY - box.height / 2 };
}

/** Visible top-left of the rotated box → stored anchor (un-rotated top-left). */
export function visualTopLeftToAnchor(
  visualX: number,
  visualY: number,
  width: number,
  height: number,
  rotationDeg: number,
): { x: number; y: number } {
  const box = rotatedAabbSize(width, height, rotationDeg);
  const centerX = visualX + box.width / 2;
  const centerY = visualY + box.height / 2;
  return { x: centerX - width / 2, y: centerY - height / 2 };
}

/**
 * Smallest stored anchor (per axis) that keeps the item's *visible* box flush
 * against the top/left walls — i.e. the anchor whose visible top-left is (0, 0).
 * For an un-rotated or 180° item this is {0, 0}; for a 12ft × 3ft table rotated
 * 90° it is {x: -4.5ft, y: +4.5ft} (in px), because the un-rotated anchor sits
 * off the visible box. Positions must be floored here rather than at 0 so a
 * rotated non-square item can actually reach the wall.
 */
export function visualAnchorFloor(
  width: number,
  height: number,
  rotationDeg: number,
): { x: number; y: number } {
  return visualTopLeftToAnchor(0, 0, width, height, rotationDeg);
}

// ─── Conversion: Backend FloorPlanObject ↔ CanvasItem ──────────
//
// Tables and non-table objects all live in `floor_plan_objects` now. An
// object is treated as a seatable table when its `tableNumber` is set.

import {
  calculateHalfRoundChairs,
  calculateRoundTableChairs,
  calculateRectTableChairs,
  calculateSerpentineChairs,
  anyChairsOverlap,
  resolveFloorPlanShape,
  resolveSerpentine,
  resolveSerpentineChairSides,
  serpentineBox,
  type SerpentineChairSides,
  type BulkUpsertObjectsInput,
  type ChairPosition,
  type ChairSides,
  type FloorPlanObject,
} from '@peles/shared';
import type { CanvasItem } from './types';
import { TABLE_TYPE_REVERSE, isTableItem } from './types';

// ─── Item identity ──────────────────────────────────────────────
//
// Every canvas item carries a `uid`: a value that is unique within the plan
// and never reused. Links between items (a chair pointing at its table, a
// group of items that move together) are keyed off it. They used to be keyed
// off `tableNumber` / `sortOrder`, both of which repeat — a renumbered table
// or an object added after a deletion could take a number another item had
// already handed out, and the two silently became one item as far as moving,
// chair placement and deletion were concerned.

let uidCounter = 0;

export function newUid(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  uidCounter += 1;
  return `u${Date.now().toString(36)}-${uidCounter.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Group id for a parent and the chairs it owns (auto-created, not user-made). */
export function newChairGroupId(): string {
  return `chairs-${newUid()}`;
}

/** Group id for an explicit user grouping ("Group" in the properties panel). */
export function newUserGroupId(): string {
  return `group-${newUid()}`;
}

/** True for the auto group that ties a parent to its own chairs. */
export function isChairGroupId(groupId: string | undefined): boolean {
  return !!groupId?.startsWith('chairs-');
}

/**
 * Config for a copy of `item`: everything except the keys that make the copy
 * a duplicate identity rather than a new item — its uid, its group membership
 * and (for a copied chair) its link back to the original's parent.
 */
export function copyItemConfig(config: Record<string, unknown>): Record<string, unknown> {
  const clean = { ...config };
  delete clean.uid;
  delete clean.groupId;
  delete clean.parentUid;
  delete clean.parentTableNumber;
  delete clean.parentObjectSort;
  return clean;
}

export function fromFloorPlanObject(o: FloorPlanObject): CanvasItem {
  const config = (o.config as Record<string, unknown>) ?? {};
  // Prefer the top-level column; fall back to a legacy config.tableNumber for
  // rows that pre-date the unification migration and haven't been backfilled
  // yet (e.g. local dev DBs that haven't run the latest migration).
  const tableNumber =
    typeof o.tableNumber === 'number'
      ? o.tableNumber
      : typeof config.tableNumber === 'number'
        ? (config.tableNumber as number)
        : undefined;
  // Strip tableNumber out of the canvas-side config so we never round-trip
  // it back to the JSON column on save.
  // `uid` and `groupId` live in the JSON column but are item fields on the
  // canvas side — they must be removed from the config we keep, or a stale
  // copy round-trips back on save and resurrects a group the user removed.
  const cleanConfig: Record<string, unknown> = { ...config };
  delete cleanConfig.tableNumber;
  delete cleanConfig.uid;
  delete cleanConfig.groupId;
  return {
    id: o.id,
    uid: typeof config.uid === 'string' && config.uid ? config.uid : o.id || newUid(),
    objectType: o.objectType,
    label: o.label || '',
    x: o.x,
    y: o.y,
    widthFt: o.widthFt,
    heightFt: o.heightFt,
    rotation: o.rotation,
    color: o.color,
    opacity: o.opacity,
    seats: o.seats ?? 0,
    config: cleanConfig,
    sortOrder: o.sortOrder,
    ...(typeof tableNumber === 'number' ? { tableNumber } : {}),
    // groupId is still persisted inside config (read from the raw config —
    // cleanConfig no longer carries it).
    ...(typeof config.groupId === 'string' ? { groupId: config.groupId } : {}),
  };
}

/**
 * Convert canvas items to the bulk-upsert payload. Folds the UI-only
 * `groupId` into `config` so it round-trips through the JSON column.
 * Pass `stripIds` when saving into a different floor plan (e.g. Save As) —
 * the DB ids belong to the source plan's objects.
 */
export function itemsToObjectsPayload(
  items: CanvasItem[],
  opts?: { stripIds?: boolean },
): BulkUpsertObjectsInput['objects'] {
  return items.map((it) => {
    const config: Record<string, unknown> = { ...it.config, uid: it.uid };
    // Absent groupId must be written as absent, not left to a leftover key.
    if (it.groupId) config.groupId = it.groupId;
    else delete config.groupId;
    const o = toFloorPlanObject({ ...it, config });
    return {
      ...o,
      id: opts?.stripIds ? undefined : o.id,
      label: o.label ?? undefined,
      seats: o.seats ?? undefined,
      tableNumber: o.tableNumber ?? undefined,
      config: o.config ?? undefined,
    };
  });
}

// ─── Chair attachment ───────────────────────────────────────────
//
// Placed chairs link to their parent through config.parentUid. Every
// operation that moves, deletes, or re-seats a parent must carry its chairs
// along, so the linkage logic lives here rather than being re-derived at each
// call site. Legacy rows link by table number / sort order instead and are
// converted once, on load, by migrateLoadedItems.

export const CHAIR_COLOR = '#9ca3af';

export const CHAIR_PARENT_FIELD = 'parentUid';

export function chairParentRef(parent: CanvasItem): {
  field: typeof CHAIR_PARENT_FIELD;
  key: string;
} {
  return { field: CHAIR_PARENT_FIELD, key: parent.uid };
}

export function isChairOf(chair: CanvasItem, parent: CanvasItem): boolean {
  if (chair.objectType !== 'CHAIR' || parent.objectType === 'CHAIR') return false;
  if (!parent.uid) return false;
  return chair.config?.[CHAIR_PARENT_FIELD] === parent.uid;
}

/** The chairs attached to `parent`, in item order. */
export function chairsOf(items: CanvasItem[], parent: CanvasItem): CanvasItem[] {
  return items.filter((it) => isChairOf(it, parent));
}

export function canParentChairs(item: CanvasItem): boolean {
  return item.objectType !== 'CHAIR' && (isTableItem(item) || !!item.config?.canHaveChairs);
}

/** Indexes of all chairs attached to any of the given parents. */
export function attachedChairIndexes(items: CanvasItem[], parents: CanvasItem[]): Set<number> {
  const result = new Set<number>();
  const nonChairParents = parents.filter((p) => p && p.objectType !== 'CHAIR');
  if (nonChairParents.length === 0) return result;
  items.forEach((it, i) => {
    if (nonChairParents.some((p) => isChairOf(it, p))) result.add(i);
  });
  return result;
}

/** Whether chairs should be laid out radially (round/oval outline). */
export function isRoundParent(item: CanvasItem): boolean {
  const tableType = isTableItem(item)
    ? TABLE_TYPE_REVERSE[item.objectType] || item.objectType.replace('TABLE_', '')
    : null;
  const shape = resolveFloorPlanShape(item.objectType, item.config);
  return tableType === 'ROUND' || tableType === 'OVAL' || shape === 'CIRCLE' || shape === 'OVAL';
}

/** Whether an item is drawn as a serpentine (curved banquet) table. */
export function isSerpentineItem(item: CanvasItem): boolean {
  return resolveFloorPlanShape(item.objectType, item.config) === 'SERPENTINE';
}

/** Whether an item is drawn as a half-round table (flat edge up, curve below). */
export function isHalfRoundItem(item: CanvasItem): boolean {
  return resolveFloorPlanShape(item.objectType, item.config) === 'HALF_ROUND';
}

/** A serpentine setting as the producer typed it, if they have. */
export function serpentineSetting(
  item: CanvasItem,
  key: 'serpentineDepthFt' | 'serpentineSweepDeg',
): number | undefined {
  const value = item.config?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export interface SerpentinePatch {
  radiusFt?: number;
  depthFt?: number;
  sweepDeg?: number;
  chairSides?: SerpentineChairSides;
}

/**
 * A serpentine after an edit to its radius, depth, arc or chair sides.
 *
 * Depth and arc are stored as typed and clamped only where they are drawn, so
 * a half-typed value ("1" on the way to "120") never overwrites them with its
 * limit. A geometry edit resizes the box and keeps any vertical stretch the
 * table already had; a chair-sides edit leaves the box alone.
 */
export function applySerpentinePatch(item: CanvasItem, patch: SerpentinePatch): CanvasItem {
  const geometry = resolveSerpentine(item.widthFt, item.heightFt, item.config);
  const depthFt = patch.depthFt ?? serpentineSetting(item, 'serpentineDepthFt') ?? geometry.depthFt;
  const sweepDeg =
    patch.sweepDeg ?? serpentineSetting(item, 'serpentineSweepDeg') ?? geometry.sweepDeg;
  const config = {
    ...item.config,
    serpentineDepthFt: depthFt,
    serpentineSweepDeg: sweepDeg,
    serpentineChairSides: patch.chairSides ?? resolveSerpentineChairSides(item.config),
  };
  const reshaped =
    patch.radiusFt !== undefined || patch.depthFt !== undefined || patch.sweepDeg !== undefined;
  if (!reshaped) return { ...item, config };
  const box = serpentineBox(patch.radiusFt ?? geometry.radiusFt, depthFt, sweepDeg);
  return {
    ...item,
    widthFt: box.widthFt,
    heightFt: box.heightFt * geometry.stretchY,
    config,
  };
}

/**
 * Where a table's chairs go, in feet. The one answer the canvas preview, the
 * chair builder and the 3D view all ask, so a serpentine is seated along its
 * curve everywhere rather than around its bounding box.
 *
 * `centered` positions are chair centres relative to the table's centre (round
 * and serpentine tables); otherwise each is the chair's top-left relative to
 * the table's top-left (the rectangle helper's frame).
 */
export function tableChairLayout(
  item: CanvasItem,
  opts: { seats?: number; chairSizeFt: number; chairSides: ChairSides },
): { positions: ChairPosition[]; centered: boolean } {
  const seats = opts.seats ?? item.seats;
  if (isSerpentineItem(item)) {
    return {
      positions: calculateSerpentineChairs(
        item.widthFt,
        item.heightFt,
        item.config,
        seats,
        opts.chairSizeFt,
      ),
      centered: true,
    };
  }
  if (isHalfRoundItem(item)) {
    return {
      positions: calculateHalfRoundChairs(item.widthFt, item.heightFt, seats, opts.chairSizeFt),
      centered: true,
    };
  }
  if (isRoundParent(item)) {
    return {
      positions: calculateRoundTableChairs(
        Math.max(item.widthFt, item.heightFt),
        seats,
        opts.chairSizeFt,
      ),
      centered: true,
    };
  }
  return {
    positions: calculateRectTableChairs(
      item.widthFt,
      item.heightFt,
      seats,
      opts.chairSizeFt,
      opts.chairSides,
    ),
    centered: false,
  };
}

/**
 * Which sides of a rectangular table its placed chairs are on, read from the
 * chairs themselves: each faces the table, so its turn relative to the table
 * says the side (0° top, 90° right, 180° bottom, 270° left). `null` when
 * there are none, or one is turned to no side at all (moved by hand). Only
 * tables saved before they remembered their sides are read this way.
 */
export function placedChairSides(parent: CanvasItem, chairs: CanvasItem[]): ChairSides | null {
  if (chairs.length === 0) return null;
  const sides = new Set<number>();
  for (const chair of chairs) {
    const turn = (((chair.rotation - parent.rotation) % 360) + 360) % 360;
    const side = (Math.round(turn / 90) * 90) % 360;
    if (Math.abs(turn - side) > 10 && Math.abs(turn - side - 360) > 10) return null;
    sides.add(side);
  }
  const has = (deg: number) => sides.has(deg);
  const key = [has(0), has(90), has(180), has(270)].map(Number).join('');
  // Some few-chair layouts are drawn by more than one choice, so the chairs
  // cannot say which was made: one chair on top (Top, Top + Bottom, All
  // Sides), one on the left (Left, Left + Right), one on top and one on the
  // bottom (Top + Bottom, All Sides). They say nothing, rather than recording
  // a guess that would put the next seat on the wrong side.
  const ambiguous =
    (chairs.length === 1 && (key === '1000' || key === '0001')) ||
    (chairs.length === 2 && key === '1010');
  if (ambiguous) return null;
  const byKey: Record<string, ChairSides> = {
    '1000': 'top',
    '0100': 'right',
    '0010': 'bottom',
    '0001': 'left',
    '1010': 'top-bottom',
    '0101': 'left-right',
    '1111': 'all',
    // All Sides fills top, bottom, then left, then right: too few seats to
    // reach the right side leaves exactly this, and no other layout does.
    '1011': 'all',
  };
  // Anything else — two sides meeting at a corner, a different three — no
  // layout draws; the chairs were moved by hand, so their sides say nothing.
  return byKey[key] ?? null;
}

/** Where a table remembers the sides its chairs were laid out on. */
export const TABLE_CHAIR_SIDES = 'chairSides';

const CHAIR_SIDES_VALUES: readonly ChairSides[] = [
  'all',
  'top',
  'bottom',
  'left',
  'right',
  'top-bottom',
  'left-right',
];

/**
 * The sides this table's chairs go on. Chair Sides is one control for the
 * whole page, so a table cannot be judged or rebuilt by whatever it says now
 * — that was chosen for whichever table was selected last. A table keeps its
 * own (`config.chairSides`, written whenever its chairs are placed or laid
 * out again); one saved before it did is read from its placed chairs; only a
 * table with neither takes `fallback`, the sides its first chairs would get.
 */
export function tableChairSides(
  item: CanvasItem,
  fallback: ChairSides,
  placed: CanvasItem[] = [],
): ChairSides {
  const stored = item.config?.[TABLE_CHAIR_SIDES];
  if (typeof stored === 'string' && (CHAIR_SIDES_VALUES as readonly string[]).includes(stored)) {
    return stored as ChairSides;
  }
  return placedChairSides(item, placed) ?? fallback;
}

/** `item` recording the sides its chairs are laid out on. */
export function withChairSides(item: CanvasItem, sides: ChairSides): CanvasItem {
  if (item.config?.[TABLE_CHAIR_SIDES] === sides) return item;
  return { ...item, config: { ...item.config, [TABLE_CHAIR_SIDES]: sides } };
}

/**
 * How many chairs this table really seats at its size, next to how many it
 * is set to. Seats are never capped — a producer who sets more than fit gets
 * them all, crowded, and is told — so this only answers the question.
 *
 * A count fits when its chairs, laid out exactly as `tableChairLayout` lays
 * them, neither overlap one another (`anyChairsOverlap`, which allows corners
 * to graze) nor, along a rectangle, run past the end of the side they sit on
 * — the rectangle layout packs extra chairs edge to edge instead of letting
 * them overlap, so overlap alone never notices a side that is full. `reason`
 * says which of the two the seats as set run into, so the warning names the
 * right one. The largest count that fits is found by halving (fitting is
 * monotone in practice: fewer chairs on the same table only spread out).
 */
export function tableSeatCapacity(
  item: CanvasItem,
  opts: { chairSizeFt: number; chairSides: ChairSides },
): {
  seats: number;
  fits: number;
  overCapacity: boolean;
  reason: 'overlap' | 'pastEnds' | null;
} {
  const seats = Math.max(0, Math.floor(item.seats));
  const problemWith = (n: number): 'overlap' | 'pastEnds' | null => {
    // One chair cannot overlap another, but it can still be wider than the
    // side it sits on, so it is measured like any other count.
    if (n <= 0) return null;
    const { positions, centered } = tableChairLayout(item, { ...opts, seats: n });
    const size = opts.chairSizeFt;
    const eps = size * 0.05;
    const chairs = positions.map((p) =>
      centered ? p : { x: p.x + size / 2, y: p.y + size / 2, rotation: p.rotation },
    );
    if (anyChairsOverlap(chairs, size)) return 'overlap';
    if (!centered) {
      const pastEnds = positions.some((p) => {
        const alongTop = p.rotation === 0 || p.rotation === 180;
        const start = alongTop ? p.x : p.y;
        const edge = alongTop ? item.widthFt : item.heightFt;
        return start < -eps || start + size > edge + eps;
      });
      if (pastEnds) return 'pastEnds';
    }
    return null;
  };
  const reason = problemWith(seats);
  if (!reason) return { seats, fits: seats, overCapacity: false, reason: null };
  let lo = 0;
  let hi = seats - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (!problemWith(mid)) lo = mid;
    else hi = mid - 1;
  }
  return { seats, fits: lo, overCapacity: true, reason };
}

/**
 * Build the CHAIR items for a parent at its current position/rotation.
 * Chair centers are rotated around the table center so chairs track the
 * parent's rotation.
 */
export function buildChairsForParent(
  parent: CanvasItem,
  opts: {
    chairSizeFt: number;
    chairSides: ChairSides;
    sortBase: number;
    groupId: string;
    seats?: number;
  },
): CanvasItem[] {
  const seats = opts.seats ?? parent.seats;
  const { field, key } = chairParentRef(parent);
  const { positions, centered: round } = tableChairLayout(parent, {
    seats,
    chairSizeFt: opts.chairSizeFt,
    chairSides: opts.chairSides,
  });

  const centerX = parent.x + ftToPx(parent.widthFt) / 2;
  const centerY = parent.y + ftToPx(parent.heightFt) / 2;
  const rotRad = (parent.rotation * Math.PI) / 180;
  const cSzPx = ftToPx(opts.chairSizeFt);
  const halfTable = { x: ftToPx(parent.widthFt) / 2, y: ftToPx(parent.heightFt) / 2 };

  return positions.map((pos, i) => {
    const chairCenterX = round ? ftToPx(pos.x) : ftToPx(pos.x) + cSzPx / 2 - halfTable.x;
    const chairCenterY = round ? ftToPx(pos.y) : ftToPx(pos.y) + cSzPx / 2 - halfTable.y;
    const rx = chairCenterX * Math.cos(rotRad) - chairCenterY * Math.sin(rotRad);
    const ry = chairCenterX * Math.sin(rotRad) + chairCenterY * Math.cos(rotRad);
    return {
      objectType: 'CHAIR',
      label: `S${i + 1}`,
      x: centerX + rx - cSzPx / 2,
      y: centerY + ry - cSzPx / 2,
      widthFt: opts.chairSizeFt,
      heightFt: opts.chairSizeFt,
      rotation: Math.round(pos.rotation + parent.rotation) % 360,
      color: CHAIR_COLOR,
      opacity: 1,
      seats: 1,
      uid: newUid(),
      config: { [field]: key },
      sortOrder: opts.sortBase + i,
      groupId: opts.groupId,
    };
  });
}

/**
 * Remove and rebuild the chairs of the parents at the given indexes, using
 * each parent's CURRENT seats/size/position. Parents that have no chairs are
 * skipped unless `placeIfMissing`. Chair size defaults to the parent's
 * existing chair size so a seats-only change keeps the configuration.
 */
export function replaceChairsForParents(
  items: CanvasItem[],
  parentIdxs: number[],
  opts: {
    /** The sides for a table that has none of its own yet (see `tableChairSides`). */
    chairSides: ChairSides;
    /** Lay these tables out on these sides — the producer just chose them. */
    sides?: ChairSides;
    defaultChairSizeFt: number;
    chairSizeFt?: number;
    placeIfMissing?: boolean;
    /**
     * A re-layout that adds no seat (a new shape, say) keeps the gaps a
     * producer left: a seat below the highest one still standing whose chair
     * was removed by hand — a wheelchair space — is not put back.
     */
    keepGaps?: boolean;
  },
): CanvasItem[] {
  const parents = parentIdxs
    .map((i) => items[i])
    .filter((p): p is CanvasItem => !!p && canParentChairs(p));
  if (parents.length === 0) return items;

  const plans: {
    parent: CanvasItem;
    chairSizeFt: number;
    groupId: string;
    sides: ChairSides;
  }[] = [];
  const chairsToRemove = new Set<CanvasItem>();
  parents.forEach((parent) => {
    const existing = items.filter((it) => isChairOf(it, parent));
    if (existing.length === 0 && !opts.placeIfMissing) return;
    existing.forEach((c) => chairsToRemove.add(c));
    plans.push({
      parent,
      chairSizeFt: opts.chairSizeFt ?? existing[0]?.widthFt ?? opts.defaultChairSizeFt,
      groupId: parent.groupId || newChairGroupId(),
      sides: opts.sides ?? tableChairSides(parent, opts.chairSides, existing),
    });
  });
  if (plans.length === 0) return items;

  let result = items
    .filter((it) => !chairsToRemove.has(it))
    .map((it) => {
      const plan = plans.find((p) => p.parent === it);
      if (!plan) return it;
      let updated = withChairSides(
        it.groupId !== plan.groupId ? { ...it, groupId: plan.groupId } : it,
        plan.sides,
      );
      if (updated.config?.[CHAIRS_REMOVED_BY_HAND]) {
        // Its chairs are placed again, so the mark has done its job.
        const config = { ...updated.config };
        delete config[CHAIRS_REMOVED_BY_HAND];
        updated = { ...updated, config };
      }
      plan.parent = updated;
      return updated;
    });
  plans.forEach(({ parent, chairSizeFt, groupId, sides }) => {
    const previous = items.filter((it) => isChairOf(it, parent)).sort(bySeat);
    const seats = new Map<number, CanvasItem>();
    const unnumbered: CanvasItem[] = [];
    for (const chair of previous) {
      const n = seatNumberOf(chair);
      if (n !== null && !seats.has(n)) seats.set(n, chair);
      else unnumbered.push(chair);
    }
    const built = buildChairsForParent(parent, {
      chairSizeFt,
      chairSides: sides,
      sortBase: result.length,
      groupId,
    });
    // The chairs that were there stay the same chairs, seat for seat: a
    // re-layout moves them, and only the seats added or taken away are new
    // or gone. Rebuilding every chair gave each a new identity, so a change
    // of one seat deleted and re-created the whole table's chairs on save
    // and lost anything set on a single chair. Seat k goes to the chair
    // labelled S<k> — matched by number, not position, so a table whose S2
    // was removed keeps S3 as S3 (the guest seated at 3 is still at 3) and
    // gets a new S2 when a seat is added back; seats past the new count are
    // the ones that go. Unnumbered chairs fill the seats nobody holds.
    const highest = Math.max(0, ...seats.keys());
    const kept = built.flatMap((chair, i) => {
      const old = seats.get(i + 1) ?? unnumbered.shift();
      if (!old) return opts.keepGaps && i + 1 < highest ? [] : [chair];
      return [
        {
          ...chair,
          ...(old.id ? { id: old.id } : {}),
          uid: old.uid,
          color: old.color,
          opacity: old.opacity,
          sortOrder: old.sortOrder,
          config: { ...old.config, ...chair.config },
        },
      ];
    });
    result = [...result, ...kept];
  });
  return result;
}

/** A chair's seat number from its `S<n>` label, for keeping seats in order. */
export function seatNumberOf(chair: CanvasItem): number | null {
  const m = /^S(\d+)$/.exec(chair.label ?? '');
  return m ? Number(m[1]) : null;
}

function bySeat(a: CanvasItem, b: CanvasItem): number {
  const sa = seatNumberOf(a);
  const sb = seatNumberOf(b);
  if (sa !== null && sb !== null && sa !== sb) return sa - sb;
  if (sa !== null && sb === null) return -1;
  if (sa === null && sb !== null) return 1;
  return a.sortOrder - b.sortOrder;
}

/**
 * Set on a table whose chairs were removed one by one down to none, so that
 * adding a seat back places a chair rather than leaving a count with nothing
 * drawn — a table never given chairs keeps showing them as a preview.
 */
export const CHAIRS_REMOVED_BY_HAND = 'chairsRemovedByHand';

/**
 * Remove items, cascading to the chairs of any table removed. A table that
 * loses chairs of its own and stays keeps the others where they stand; its
 * seat count drops only as far as the highest seat number still standing,
 * so a gap in the middle stays a numbered seat without a chair.
 */
export function removeItemsWithSeats(items: CanvasItem[], remove: Iterable<number>): CanvasItem[] {
  const gone = new Set(remove);
  const removedParents = [...gone].map((i) => items[i]).filter((it): it is CanvasItem => !!it);
  attachedChairIndexes(items, removedParents).forEach((i) => gone.add(i));
  const seatsLost = new Map<string, number>();
  gone.forEach((i) => {
    const it = items[i];
    const parentUid = it?.objectType === 'CHAIR' ? it.config?.[CHAIR_PARENT_FIELD] : undefined;
    if (typeof parentUid === 'string') {
      seatsLost.set(parentUid, (seatsLost.get(parentUid) ?? 0) + 1);
    }
  });
  const kept = items.filter((_, i) => !gone.has(i));
  return kept.map((it) => {
    const lost = seatsLost.get(it.uid);
    if (!lost) return it;
    const remaining = kept.filter((c) => isChairOf(c, it));
    if (remaining.length === 0) {
      // Every chair gone: no seats left. Subtracting only the chairs removed
      // now left a count behind for seats emptied earlier as gaps.
      return {
        ...it,
        seats: 0,
        config: { ...it.config, [CHAIRS_REMOVED_BY_HAND]: true },
      };
    }
    // The table keeps every seat number a chair still carries: taking S2 out
    // of S1–S4 leaves a gap at 2, not a three-seat table with a chair called
    // S4 — which the seat assignments would not offer and the next re-layout
    // would drop, taking the guest at 4 with it. Only removing the highest
    // seats lowers the count.
    const highest = Math.max(0, ...remaining.map((c) => seatNumberOf(c) ?? 0));
    return { ...it, seats: Math.min(it.seats, Math.max(highest, remaining.length)) };
  });
}

/**
 * Apply per-item position deltas, extending each delta to the item's
 * attached chairs (unless the chair has its own delta already). Each axis is
 * clamped so the item's *visible* bounding box cannot cross the top/left wall
 * (`visualAnchorFloor`) — for un-rotated/square items this is the usual floor
 * at 0, but a rotated non-square item can now reach the wall (its stored anchor
 * goes negative). Only axes with a non-zero delta are clamped, so editing one
 * coordinate never nudges the other.
 */
export function moveItemsWithChairs(
  items: CanvasItem[],
  deltas: Map<number, { dx: number; dy: number }>,
): CanvasItem[] {
  const extended = new Map(deltas);
  deltas.forEach((d, idx) => {
    const parent = items[idx];
    if (!parent || parent.objectType === 'CHAIR') return;
    items.forEach((it, i) => {
      if (!extended.has(i) && isChairOf(it, parent)) extended.set(i, d);
    });
  });
  return items.map((it, i) => {
    const d = extended.get(i);
    if (!d || (d.dx === 0 && d.dy === 0)) return it;
    const floor = visualAnchorFloor(ftToPx(it.widthFt), ftToPx(it.heightFt), it.rotation);
    return {
      ...it,
      x: d.dx !== 0 ? Math.max(floor.x, it.x + d.dx) : it.x,
      y: d.dy !== 0 ? Math.max(floor.y, it.y + d.dy) : it.y,
    };
  });
}

/**
 * Expand a seed selection to everything that moves with it: whole groups of
 * any member (transitively) and the attached chairs of any non-chair member.
 */
export function expandMoveSet(items: CanvasItem[], seed: Iterable<number>): Set<number> {
  const set = new Set(seed);
  // New entries added during forEach are visited too, so group expansion is
  // transitive.
  set.forEach((mi) => {
    const item = items[mi];
    if (item?.groupId) {
      items.forEach((it, i) => {
        if (it.groupId === item.groupId) set.add(i);
      });
    }
  });
  const parents = [...set].map((i) => items[i]).filter((it): it is CanvasItem => !!it);
  attachedChairIndexes(items, parents).forEach((i) => set.add(i));
  return set;
}

/**
 * Normalise the items of a freshly loaded plan.
 *
 * Older rows link a chair to its parent by table number or by sort order, and
 * older auto-groups were named after those same numbers
 * (`chairs-parentTableNumber-5-restored`) — so any two tables that ever wore
 * number 5, or any two objects that ended up with the same sort order, were
 * read back as one group with one shared set of chairs. This runs once per
 * plan load and rewrites both onto uids:
 *
 *  - every legacy chair link is resolved to a single parent (the closest one
 *    when the old key is ambiguous) and stored as `config.parentUid`;
 *  - every merged auto chair-group is rebuilt with a fresh, unique id
 *    covering one parent and its own chairs, which un-merges plans already
 *    corrupted in production. Groups the user made by hand (`group-…`), and
 *    auto groups that are already intact, are left alone.
 *
 * It reports whether it changed anything, so the caller can persist a repair
 * once rather than re-deriving it on every load.
 */
export function migrateLoadedItems(items: CanvasItem[]): {
  items: CanvasItem[];
  changed: boolean;
} {
  // Whether anything was actually repaired. A plan that is already healthy
  // must come back untouched, so opening one never looks like an edit — and a
  // plan that was repaired is marked dirty by the caller so the repair reaches
  // the database instead of being re-derived (differently, once items have
  // moved) on every future load.
  let changed = false;

  // 1 — every item gets a uid that is unique inside the plan.
  const takenUids = new Set<string>();
  const withUids = items.map((it) => {
    const uid = it.uid && !takenUids.has(it.uid) ? it.uid : newUid();
    takenUids.add(uid);
    if (uid === it.uid) return it;
    changed = true;
    return { ...it, uid };
  });

  const centerOf = (it: CanvasItem) => ({
    x: it.x + ftToPx(it.widthFt) / 2,
    y: it.y + ftToPx(it.heightFt) / 2,
  });
  const nearest = (candidates: CanvasItem[], chair: CanvasItem): string | undefined => {
    if (candidates.length === 0) return undefined;
    if (candidates.length === 1) return candidates[0].uid;
    const c = centerOf(chair);
    let best = candidates[0];
    let bestDist = Infinity;
    candidates.forEach((cand) => {
      const cc = centerOf(cand);
      const d = (cc.x - c.x) ** 2 + (cc.y - c.y) ** 2;
      if (d < bestDist) {
        bestDist = d;
        best = cand;
      }
    });
    return best.uid;
  };

  // 2 — resolve chair → parent links onto uids, dropping the legacy keys.
  const resolved = withUids.map((it) => {
    if (it.objectType !== 'CHAIR') return it;
    const cfg = it.config ?? {};
    const linked =
      typeof cfg[CHAIR_PARENT_FIELD] === 'string' &&
      withUids.some((cand) => cand.objectType !== 'CHAIR' && cand.uid === cfg[CHAIR_PARENT_FIELD]);
    let parentUid = linked ? (cfg[CHAIR_PARENT_FIELD] as string) : undefined;
    if (!parentUid && typeof cfg.parentTableNumber === 'number') {
      parentUid = nearest(
        withUids.filter((cand) => isTableItem(cand) && cand.tableNumber === cfg.parentTableNumber),
        it,
      );
    }
    if (!parentUid && typeof cfg.parentObjectSort === 'number') {
      parentUid = nearest(
        withUids.filter(
          (cand) =>
            cand.objectType !== 'CHAIR' &&
            !isTableItem(cand) &&
            cand.sortOrder === cfg.parentObjectSort,
        ),
        it,
      );
    }
    const config = { ...cfg };
    delete config.parentTableNumber;
    delete config.parentObjectSort;
    if (parentUid) config[CHAIR_PARENT_FIELD] = parentUid;
    else delete config[CHAIR_PARENT_FIELD];
    if (
      'parentTableNumber' in cfg ||
      'parentObjectSort' in cfg ||
      config[CHAIR_PARENT_FIELD] !== cfg[CHAIR_PARENT_FIELD]
    ) {
      changed = true;
      return { ...it, config };
    }
    return it;
  });

  // 3 — rebuild the auto chair-groups, one fresh id per parent.
  const chairIdxByParent = new Map<string, number[]>();
  resolved.forEach((it, i) => {
    if (it.objectType !== 'CHAIR') return;
    const parentUid = it.config?.[CHAIR_PARENT_FIELD];
    if (typeof parentUid !== 'string') return;
    const list = chairIdxByParent.get(parentUid);
    if (list) list.push(i);
    else chairIdxByParent.set(parentUid, [i]);
  });

  const result = [...resolved];
  resolved.forEach((parent, i) => {
    if (parent.objectType === 'CHAIR') return;
    const chairIdxs = chairIdxByParent.get(parent.uid) ?? [];
    if (chairIdxs.length === 0) {
      if (isChairGroupId(parent.groupId)) {
        result[i] = { ...parent, groupId: undefined };
        changed = true;
      }
      return;
    }
    // A hand-made group the parent belongs to still wins — its chairs join it.
    // An auto group is kept when it already covers exactly this parent and its
    // own chairs; only a merged (or missing) one is re-minted, so a healthy
    // plan is left byte-identical.
    const current = parent.groupId;
    const owned = new Set([i, ...chairIdxs]);
    const intact =
      !!current &&
      (!isChairGroupId(current) ||
        resolved.every((it, oi) => (it.groupId === current) === owned.has(oi)));
    const gid = intact ? (current as string) : newChairGroupId();
    if (result[i].groupId !== gid) {
      result[i] = { ...result[i], groupId: gid };
      changed = true;
    }
    chairIdxs.forEach((ci) => {
      if (result[ci].groupId !== gid) {
        result[ci] = { ...result[ci], groupId: gid };
        changed = true;
      }
    });
  });
  // Chairs left without a parent must not drag a stale group along.
  result.forEach((it, i) => {
    if (
      it.objectType === 'CHAIR' &&
      typeof it.config?.[CHAIR_PARENT_FIELD] !== 'string' &&
      isChairGroupId(it.groupId)
    ) {
      result[i] = { ...it, groupId: undefined };
      changed = true;
    }
  });
  return { items: result, changed };
}

export function toFloorPlanObject(
  item: CanvasItem,
): Omit<FloorPlanObject, 'id' | 'floorPlanId' | 'createdAt' | 'updatedAt'> & { id?: string } {
  return {
    id: item.id,
    objectType: item.objectType as FloorPlanObject['objectType'],
    label: item.label || null,
    x: item.x,
    y: item.y,
    widthFt: item.widthFt,
    heightFt: item.heightFt,
    rotation: item.rotation,
    color: item.color,
    opacity: item.opacity,
    seats: item.seats ?? null,
    tableNumber: typeof item.tableNumber === 'number' ? item.tableNumber : null,
    config: Object.keys(item.config).length > 0 ? item.config : null,
    sortOrder: item.sortOrder,
  };
}
